import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTheme } from '../../../assets/contexts/ThemeContext';

/**
 * 최상단 영역 제어 컴포넌트
 * 원본: header-top-control.html
 * 원본의 모든 로직을 그대로 React 방식으로 변환
 */
const HeaderTopControl = () => {
  const themeContext = useTheme();
  const { themeConfig, set } = themeContext || {};

  // 원본과 동일한 기본값
  const defaultBackgroundColor = '#f8f8f8';
  const defaultTextColor = '#000000';
  const defaultHeight = 10;
  const defaultVisible = true;

  // Refs (모든 hooks는 조건부 반환 이전에 호출되어야 함)
  const bgColorInputRef = useRef(null);
  const bgColorTextInputRef = useRef(null);
  const textColorInputRef = useRef(null);
  const textColorTextInputRef = useRef(null);
  const heightInputRef = useRef(null);
  const visibleCheckboxRef = useRef(null);
  const visibleToggleTrackRef = useRef(null);
  const visibleToggleThumbRef = useRef(null);

  // State (모든 hooks는 조건부 반환 이전에 호출되어야 함)
  const [bgColor, setBgColor] = useState(defaultBackgroundColor);
  const [textColor, setTextColor] = useState(defaultTextColor);
  const [height, setHeight] = useState(defaultHeight);
  const [isVisible, setIsVisible] = useState(defaultVisible);
  const [bgColorTextError, setBgColorTextError] = useState(false);
  const [textColorTextError, setTextColorTextError] = useState(false);

  // 원본과 동일한 색상 검증 함수
  const isValidColor = useCallback((color) => {
    return /^#[0-9A-F]{6}$/i.test(color);
  }, []);

  // 토글 UI 업데이트 (원본 updateVisibleToggleUI와 동일)
  const updateVisibleToggleUI = useCallback((isVisibleValue) => {
    const checked = !!isVisibleValue;
    if (visibleCheckboxRef.current) {
      visibleCheckboxRef.current.checked = checked;
    }
    if (visibleToggleTrackRef.current && visibleToggleThumbRef.current) {
      if (checked) {
        visibleToggleTrackRef.current.style.backgroundColor = 'var(--theme-color-primary, #e97730)';
        visibleToggleThumbRef.current.style.transform = 'translateX(20px)';
      } else {
        visibleToggleTrackRef.current.style.backgroundColor = '#ccc';
        visibleToggleThumbRef.current.style.transform = 'translateX(0px)';
      }
    }
  }, []);

  // 배경색 적용 (원본 applyBackgroundColor와 동일)
  const applyBackgroundColor = useCallback((colorValue) => {
    if (!isValidColor(colorValue)) return;
    document.documentElement.style.setProperty('--header-top-bg-color', colorValue);
    if (set) {
      set('headerTopBackgroundColor', colorValue);
    }
  }, [isValidColor, set]);

  // 글자색 적용 (원본 applyTextColor와 동일)
  const applyTextColor = useCallback((colorValue) => {
    if (!isValidColor(colorValue)) return;
    document.documentElement.style.setProperty('--header-top-text-color', colorValue);
    if (set) {
      set('headerTopTextColor', colorValue);
    }
  }, [isValidColor, set]);

  // 높이 적용 (원본 applyHeight와 동일)
  const applyHeight = useCallback((heightValue) => {
    const numValue = parseInt(heightValue, 10);
    if (isNaN(numValue) || numValue < 0 || numValue > 100) {
      return;
    }
    const heightPx = numValue + 'px';
    document.documentElement.style.setProperty('--header-top-padding', heightPx);
    const headerTopEl = document.querySelector('.header-top');
    if (headerTopEl) {
      headerTopEl.style.paddingTop = heightPx;
      headerTopEl.style.paddingBottom = heightPx;
    }
    if (set) {
      set('headerTopHeight', numValue);
    }
  }, [set]);

  // 표시/숨김 적용 (원본 applyVisible와 동일)
  const applyVisible = useCallback((isVisibleValue, options = {}) => {
    const shouldSave = options.skipSave !== true;
    const headerTopEl = document.querySelector('.header-top');
    if (headerTopEl) {
      if (isVisibleValue) {
        headerTopEl.classList.remove('hidden');
        headerTopEl.style.removeProperty('display');
      } else {
        headerTopEl.classList.add('hidden');
        headerTopEl.style.removeProperty('display');
      }
    }
    if (shouldSave && set) {
      set('headerTopVisible', isVisibleValue);
    }

    updateVisibleToggleUI(isVisibleValue);

    // 원본과 동일하게 nav offsets 업데이트
    if (typeof window.ThemeApply === 'object' && typeof window.ThemeApply.updateNavOffsets === 'function') {
      window.ThemeApply.updateNavOffsets();
    } else if (typeof window.updateNavOffsets === 'function') {
      window.updateNavOffsets();
    }

    if (typeof window.initSidebarScrollOffset === 'function') {
      window.initSidebarScrollOffset();
    }
  }, [set, updateVisibleToggleUI]);

  // 초기화 플래그 (마운트 시에만 실행)
  const isInitializedRef = useRef(false);

  // 초기화 (원본 init 함수와 동일한 로직)
  useEffect(() => {
    // 이미 초기화되었으면 실행하지 않음 (무한 루프 방지)
    if (isInitializedRef.current) return;

    let initialBackground = defaultBackgroundColor;
    let initialText = defaultTextColor;
    let initialHeight = defaultHeight;
    let initialVisible = defaultVisible;

    // ThemeConfig에서 저장된 값 가져오기
    if (themeConfig) {
      const savedBg = themeConfig.headerTopBackgroundColor;
      if (savedBg && isValidColor(savedBg)) {
        initialBackground = savedBg;
      }
      const savedText = themeConfig.headerTopTextColor;
      if (savedText && isValidColor(savedText)) {
        initialText = savedText;
      }
      const savedHeight = themeConfig.headerTopHeight;
      if (savedHeight !== undefined && savedHeight !== null && !isNaN(parseInt(savedHeight, 10))) {
        initialHeight = parseInt(savedHeight, 10);
      }
      const savedVisible = themeConfig.headerTopVisible;
      if (savedVisible !== undefined && savedVisible !== null) {
        initialVisible = savedVisible === true || savedVisible === 'true';
      }
    }

    // CSS 변수에서 fallback 값 가져오기
    try {
      const computedStyle = getComputedStyle(document.documentElement);

      if ((!initialBackground || !isValidColor(initialBackground))) {
        const cssBg = computedStyle.getPropertyValue('--header-top-bg-color').trim();
        if (cssBg && isValidColor(cssBg)) {
          initialBackground = cssBg;
        }
      }

      if ((!initialText || !isValidColor(initialText))) {
        const cssText = computedStyle.getPropertyValue('--header-top-text-color').trim();
        if (cssText && isValidColor(cssText)) {
          initialText = cssText;
        }
      }
    } catch (e) {
      console.warn('CSS 변수 읽기 오류:', e);
    }

    // 기본값으로 fallback
    if (!initialBackground || !isValidColor(initialBackground)) {
      initialBackground = defaultBackgroundColor;
    }
    if (!initialText || !isValidColor(initialText)) {
      initialText = defaultTextColor;
    }
    if (isNaN(initialHeight) || initialHeight < 0 || initialHeight > 100) {
      initialHeight = defaultHeight;
    }

    // 초기값 적용
    setBgColor(initialBackground);
    setTextColor(initialText);
    setHeight(initialHeight);
    setIsVisible(initialVisible);

    applyVisible(initialVisible, { skipSave: true });
    applyBackgroundColor(initialBackground);
    applyTextColor(initialText);
    applyHeight(initialHeight);

    // 초기화 완료 표시
    isInitializedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 마운트 시에만 실행 (의도적으로 의존성 배열 비움 - 무한 루프 방지)

  // 배경색 변경 처리 (초기화 시에만 input 값 동기화)
  useEffect(() => {
    if (bgColorInputRef.current && bgColorTextInputRef.current) {
      const currentBgValue = bgColorInputRef.current.value;
      const currentTextValue = bgColorTextInputRef.current.value;
      // 현재 input 값과 state 값이 다를 때만 업데이트 (무한 루프 방지)
      if (currentBgValue !== bgColor) {
        bgColorInputRef.current.value = bgColor;
      }
      if (currentTextValue !== bgColor) {
        bgColorTextInputRef.current.value = bgColor;
      }
    }
    // applyBackgroundColor는 핸들러에서만 호출하여 무한 루프 방지
  }, [bgColor]);

  // 글자색 변경 처리 (초기화 시에만 input 값 동기화)
  useEffect(() => {
    if (textColorInputRef.current && textColorTextInputRef.current) {
      const currentColorValue = textColorInputRef.current.value;
      const currentTextValue = textColorTextInputRef.current.value;
      // 현재 input 값과 state 값이 다를 때만 업데이트 (무한 루프 방지)
      if (currentColorValue !== textColor) {
        textColorInputRef.current.value = textColor;
      }
      if (currentTextValue !== textColor) {
        textColorTextInputRef.current.value = textColor;
      }
    }
    // applyTextColor는 핸들러에서만 호출하여 무한 루프 방지
  }, [textColor]);

  // 높이 변경 처리 (초기화 시에만 input 값 동기화)
  useEffect(() => {
    if (heightInputRef.current) {
      const currentValue = parseInt(heightInputRef.current.value, 10);
      // 현재 input 값과 state 값이 다를 때만 업데이트 (무한 루프 방지)
      if (isNaN(currentValue) || currentValue !== height) {
        heightInputRef.current.value = height;
      }
    }
    // applyHeight는 handleHeightChange에서만 호출하여 무한 루프 방지
  }, [height]);

  // 표시/숨김 변경 처리
  useEffect(() => {
    updateVisibleToggleUI(isVisible);
    applyVisible(isVisible);
  }, [isVisible, updateVisibleToggleUI, applyVisible]);

  // 조건부 반환 (모든 hooks 호출 이후)
  if (!themeContext) return null;

  // 배경색 color input 변경
  const handleBgColorInputChange = (e) => {
    const value = e.target.value;
    setBgColor(value);
    setBgColorTextError(false);
    // 직접 applyBackgroundColor 호출하여 즉시 적용 (useEffect 무한 루프 방지)
    applyBackgroundColor(value);
  };

  // 배경색 text input 변경
  const handleBgColorTextChange = (e) => {
    const value = e.target.value.trim();
    if (isValidColor(value)) {
      setBgColor(value);
      setBgColorTextError(false);
      // 직접 applyBackgroundColor 호출하여 즉시 적용 (useEffect 무한 루프 방지)
      applyBackgroundColor(value);
    } else {
      setBgColorTextError(true);
    }
  };

  // 배경색 text input blur
  const handleBgColorTextBlur = (e) => {
    const value = e.target.value.trim();
    if (!isValidColor(value)) {
      setBgColor(bgColor); // 현재 유효한 값으로 복원
      setBgColorTextError(false);
    }
  };

  // 글자색 color input 변경
  const handleTextColorInputChange = (e) => {
    const value = e.target.value;
    setTextColor(value);
    setTextColorTextError(false);
    // 직접 applyTextColor 호출하여 즉시 적용 (useEffect 무한 루프 방지)
    applyTextColor(value);
  };

  // 글자색 text input 변경
  const handleTextColorTextChange = (e) => {
    const value = e.target.value.trim();
    if (isValidColor(value)) {
      setTextColor(value);
      setTextColorTextError(false);
      // 직접 applyTextColor 호출하여 즉시 적용 (useEffect 무한 루프 방지)
      applyTextColor(value);
    } else {
      setTextColorTextError(true);
    }
  };

  // 글자색 text input blur
  const handleTextColorTextBlur = (e) => {
    const value = e.target.value.trim();
    if (!isValidColor(value)) {
      setTextColor(textColor); // 현재 유효한 값으로 복원
      setTextColorTextError(false);
    }
  };

  // 높이 input 변경
  const handleHeightChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 0 && value <= 100) {
      setHeight(value);
      // 직접 applyHeight 호출하여 즉시 적용 (useEffect 무한 루프 방지)
      applyHeight(value);
    }
  };

  // 높이 input blur
  const handleHeightBlur = (e) => {
    const value = parseInt(e.target.value, 10);
    if (isNaN(value) || value < 0 || value > 100) {
      const currentValue = themeConfig?.headerTopHeight ?? defaultHeight;
      const validValue = (!isNaN(parseInt(currentValue, 10)) && parseInt(currentValue, 10) >= 0 && parseInt(currentValue, 10) <= 100)
        ? parseInt(currentValue, 10)
        : defaultHeight;
      setHeight(validValue);
      applyHeight(validValue);
    }
  };

  // 표시/숨김 체크박스 변경
  const handleVisibleChange = (e) => {
    setIsVisible(e.target.checked);
  };

  // 리셋 버튼 핸들러
  const handleBgReset = () => {
    setBgColor(defaultBackgroundColor);
    setBgColorTextError(false);
    applyBackgroundColor(defaultBackgroundColor);
  };

  const handleTextReset = () => {
    setTextColor(defaultTextColor);
    setTextColorTextError(false);
    applyTextColor(defaultTextColor);
  };

  const handleHeightReset = () => {
    setHeight(defaultHeight);
    applyHeight(defaultHeight);
  };

  return (
    <>
      <style>{`
        #header-top-control-section:not(:last-child) {
          padding-bottom: 15px;
        }
        .header-top-control-header {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 12px;
        }
        .header-top-control-header > label {
          display: block;
          font-weight: bold;
          color: #333;
          font-size: 13px;
          cursor: default;
          margin: 0;
          flex: 1;
        }
        #header-top-control-section .theme-help-btn {
          background: transparent;
          border: 1px solid #ddd;
          color: #666;
          font-size: 11px;
          width: 20px;
          height: 20px;
          padding: 0;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          flex-shrink: 0;
        }
        .header-top-control-description {
          font-size: 11px;
          color: #999;
          margin-bottom: 12px;
          line-height: 1.4;
        }
        .header-top-control-visible,
        .header-top-control-bg-color,
        .header-top-control-text-color {
          margin-bottom: 18px;
        }
        .header-top-control-visible-header,
        .header-top-control-bg-color-header,
        .header-top-control-text-color-header,
        .header-top-control-height-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 6px;
        }
        .header-top-control-visible-header > span {
          font-size: 12px;
          color: #666;
          font-weight: 500;
        }
        .header-top-control-visible-header > label {
          position: relative;
          display: inline-flex;
          align-items: center;
          cursor: pointer;
        }
        #header-top-visible-checkbox {
          position: absolute;
          opacity: 0;
          width: 0;
          height: 0;
          margin: 0;
        }
        #header-top-visible-toggle-track {
          width: 42px;
          height: 22px;
          background: #ccc;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          padding: 2px;
          box-sizing: border-box;
          transition: background-color 0.2s ease;
        }
        #header-top-visible-toggle-thumb {
          width: 18px;
          height: 18px;
          background: #fff;
          border-radius: 50%;
          box-shadow: 0 1px 4px rgba(0,0,0,0.2);
          transition: transform 0.2s ease;
        }
        .header-top-control-visible-description {
          font-size: 10px;
          color: #999;
          margin-top: 4px;
          line-height: 1.4;
        }
        .header-top-control-bg-color-header > label,
        .header-top-control-text-color-header > label,
        .header-top-control-height-header > label {
          font-size: 12px;
          color: #666;
          font-weight: 500;
          cursor: pointer;
        }
        #reset-header-top-bg-btn,
        #reset-header-top-text-btn,
        #reset-header-top-height-btn {
          background: transparent;
          border: 1px solid #ddd;
          color: #666;
          font-size: 10px;
          padding: 2px 8px;
          border-radius: 3px;
          cursor: pointer;
          transition: all 0.2s;
        }
        .header-top-control-bg-color-input-container,
        .header-top-control-text-color-input-container {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        #header-top-bg-color-input,
        #header-top-text-color-input {
          width: 50px;
          height: 35px;
          border: 1px solid #ddd;
          border-radius: 4px;
          cursor: pointer;
          padding: 0;
        }
        .header-top-control-bg-color-text-container,
        .header-top-control-text-color-text-container {
          flex: 1;
        }
        #header-top-bg-color-text,
        #header-top-text-color-text {
          width: 100%;
          padding: 6px 10px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 12px;
          font-family: monospace;
        }
        .header-top-control-height-input-container {
          position: relative;
        }
        #header-top-height-input {
          width: 100%;
          padding: 6px 10px;
          padding-right: 35px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 12px;
          font-family: monospace;
          text-align: center;
        }
        .header-top-control-height-unit {
          position: absolute;
          right: 8px;
          bottom: 6px;
          font-size: 11px;
          color: #999;
          pointer-events: none;
        }
        .header-top-control-height-description {
          font-size: 10px;
          color: #999;
          margin-top: 4px;
          line-height: 1.4;
        }
      `}</style>
    <div id="header-top-control-section">
        {/* 표시/숨김 제어: 최상단(첫 번째)로 위치 이동 */}
        <div className="header-top-control-visible">
          <div className="header-top-control-visible-header">
            <span>최상단 영역 표시</span>
            <label htmlFor="header-top-visible-checkbox">
              <input
                type="checkbox"
                id="header-top-visible-checkbox"
                ref={visibleCheckboxRef}
                checked={isVisible}
                onChange={handleVisibleChange}
              />
              <span
                id="header-top-visible-toggle-track"
                ref={visibleToggleTrackRef}
                style={{
                  backgroundColor: isVisible ? 'var(--theme-color-primary, #e97730)' : '#ccc'
                }}
              >
                <span
                  id="header-top-visible-toggle-thumb"
                  ref={visibleToggleThumbRef}
                  style={{
                    transform: isVisible ? 'translateX(20px)' : 'translateX(0px)'
                  }}
                ></span>
              </span>
            </label>
          </div>
          <div className="header-top-control-visible-description">
            체크 해제 시 최상단 영역이 숨겨집니다.
          </div>
        </div>

        {/* 표시가 꺼져있으면 컨테이너 너비/스타일 설정은 ThemeRemoteControl에서 숨김 처리.
            이 컴포넌트에서는 최상단 영역 스타일(배경색/글자색/높이)만 숨김 처리합니다. */}
        {isVisible && (
          <>
            <div className="header-top-control-header">
              <label>🌈 최상단 영역 스타일:</label>
              <button
                type="button"
                className="theme-help-btn"
                data-target="header-top-control-section"
                title="이 항목으로 이동"
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#f5f5f5';
                  e.target.style.borderColor = 'var(--theme-color-primary)';
                  e.target.style.color = 'var(--theme-color-primary)';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.borderColor = '#ddd';
                  e.target.style.color = '#666';
                }}
              >
                ?
              </button>
            </div>
            <div className="header-top-control-description">
              최상단 영역(.header-top)의 배경색, 글자색, 높이를 설정합니다. 입력값은 HEX 형식(#RRGGBB)이어야 합니다.
            </div>

            {/* 배경색 제어 */}
            <div className="header-top-control-bg-color">
              <div className="header-top-control-bg-color-header">
                <label htmlFor="header-top-bg-color-input">배경색:</label>
                <button
                  type="button"
                  id="reset-header-top-bg-btn"
                  onClick={handleBgReset}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#f5f5f5';
                    e.target.style.borderColor = 'var(--theme-color-primary)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.borderColor = '#ddd';
                  }}
                >
                  리셋
                </button>
              </div>
              <div className="header-top-control-bg-color-input-container">
                <input
                  type="color"
                  id="header-top-bg-color-input"
                  ref={bgColorInputRef}
                  value={bgColor}
                  onChange={handleBgColorInputChange}
                  title="최상단 영역 배경색 선택"
                />
                <div className="header-top-control-bg-color-text-container">
                  <input
                    type="text"
                    id="header-top-bg-color-text"
                    ref={bgColorTextInputRef}
                    value={bgColor}
                    onChange={handleBgColorTextChange}
                    onBlur={handleBgColorTextBlur}
                    placeholder="#f8f8f8"
                    style={{
                      borderColor: bgColorTextError ? '#ff4444' : '#ddd'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* 글자색 제어 */}
            <div className="header-top-control-text-color">
              <div className="header-top-control-text-color-header">
                <label htmlFor="header-top-text-color-input">글자색:</label>
                <button
                  type="button"
                  id="reset-header-top-text-btn"
                  onClick={handleTextReset}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#f5f5f5';
                    e.target.style.borderColor = 'var(--theme-color-primary)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.borderColor = '#ddd';
                  }}
                >
                  리셋
                </button>
              </div>
              <div className="header-top-control-text-color-input-container">
                <input
                  type="color"
                  id="header-top-text-color-input"
                  ref={textColorInputRef}
                  value={textColor}
                  onChange={handleTextColorInputChange}
                  title="최상단 영역 글자색 선택"
                />
                <div className="header-top-control-text-color-text-container">
                  <input
                    type="text"
                    id="header-top-text-color-text"
                    ref={textColorTextInputRef}
                    value={textColor}
                    onChange={handleTextColorTextChange}
                    onBlur={handleTextColorTextBlur}
                    placeholder="#000000"
                    style={{
                      borderColor: textColorTextError ? '#ff4444' : '#ddd'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* 높이 제어 */}
            <div className="header-top-control-height">
              <div className="header-top-control-height-header">
                <label htmlFor="header-top-height-input">높이 (상하 패딩):</label>
                <button
                  type="button"
                  id="reset-header-top-height-btn"
                  onClick={handleHeightReset}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#f5f5f5';
                    e.target.style.borderColor = 'var(--theme-color-primary)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.borderColor = '#ddd';
                  }}
                >
                  리셋
                </button>
              </div>
              <div className="header-top-control-height-input-container">
                <input
                  type="number"
                  id="header-top-height-input"
                  ref={heightInputRef}
                  value={height}
                  onChange={handleHeightChange}
                  onBlur={handleHeightBlur}
                  step="1"
                  min="0"
                  max="100"
                  placeholder="10"
                />
                <div className="header-top-control-height-unit">px</div>
              </div>
              <div className="header-top-control-height-description">
                상단과 하단에 동일한 패딩이 적용됩니다.
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default HeaderTopControl;
